import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import * as Sentry from "@sentry/react";
import PropTypes from "prop-types";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { LoadingButton } from "@aclymatepackages/atoms";
import {
  AGREEMENT_NOUN_BY_TIER,
  DOC_TYPE_BY_TIER
} from "@aclymatepackages/constants";

import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../helpers/analytics";

const SCROLL_TO_BOTTOM_THRESHOLD_PX = 4;
const SKIP_TO_END_DELAY_MS = 3000;

const formatIsoDate = (isoString) => {
  if (!isoString) {
    return null;
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const buildErrorCopy = (agreementNoun) =>
  `Couldn't load the ${agreementNoun} — please contact support.`;

const buildScrollHelperCaption = ({ scrolledToBottom, checkboxChecked }) => {
  if (!scrolledToBottom) {
    return "Please scroll to the bottom to enable acceptance.";
  }
  if (!checkboxChecked) {
    return "Check the box to enable acceptance.";
  }
  return null;
};

const buildTitle = ({ mode, hasDoctextError, agreementNoun, currentVersion }) => {
  if (hasDoctextError) {
    return `Couldn't load the ${agreementNoun}`;
  }
  const versionSuffix = currentVersion ? ` (v${currentVersion})` : "";
  const verb = mode === "review" ? "Review" : "Accept";
  return `${verb} the Aclymate Agent Access ${agreementNoun}${versionSuffix}`;
};

const buildReviewCaption = ({ eulaStatus }) => {
  if (!eulaStatus || eulaStatus.requiresAcceptance !== false) {
    return "You have not yet accepted this version.";
  }
  const formattedDate = formatIsoDate(eulaStatus.acceptedAt);
  const attribution = eulaStatus.acceptedByName || eulaStatus.acceptedBy;
  if (!formattedDate && !attribution) {
    return "This version has been accepted.";
  }
  if (!formattedDate) {
    return `Accepted by ${attribution}.`;
  }
  if (!attribution) {
    return `Accepted on ${formattedDate}.`;
  }
  return `Accepted on ${formattedDate} by ${attribution}.`;
};

const EulaAcceptanceModal = ({
  open,
  mode,
  tier,
  accountId,
  eulaStatus,
  analyticsEventPrefix = "api_key",
  activateSnackbar,
  onAccepted,
  onClose
}) => {
  const { palette } = useTheme();
  const scrollContainerRef = useRef(null);
  const hasFiredModalOpenedRef = useRef(false);
  const hasFiredScrolledToBottomRef = useRef(false);
  const doctextCacheRef = useRef(new Map());

  const [doctext, setDoctext] = useState(null);
  const [doctextError, setDoctextError] = useState(null);
  const [isLoadingDoctext, setIsLoadingDoctext] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showSkipToEnd, setShowSkipToEnd] = useState(false);

  const agreementNoun = AGREEMENT_NOUN_BY_TIER[tier] || "agreement";
  const docType = DOC_TYPE_BY_TIER[tier];
  const currentVersion = eulaStatus ? eulaStatus.currentVersion : null;
  const statusUnavailable = eulaStatus ? eulaStatus.statusUnavailable === true : false;
  const hasDoctextError = doctextError !== null;
  const isReviewMode = mode === "review";
  const isAcceptMode = mode === "accept";

  const resetState = useCallback(() => {
    setDoctext(null);
    setDoctextError(null);
    setIsLoadingDoctext(false);
    setScrolledToBottom(false);
    setCheckboxChecked(false);
    setIsAccepting(false);
    setShowSkipToEnd(false);
    hasFiredModalOpenedRef.current = false;
    hasFiredScrolledToBottomRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !docType) {
      return undefined;
    }
    if (statusUnavailable) {
      setDoctext(null);
      setDoctextError("eula_status_unavailable");
      setIsLoadingDoctext(false);
      return undefined;
    }
    if (!currentVersion) {
      return undefined;
    }
    const cacheKey = `${docType}_${currentVersion}`;
    const cachedText = doctextCacheRef.current.get(cacheKey);
    if (cachedText) {
      setDoctext(cachedText);
      setDoctextError(null);
      setIsLoadingDoctext(false);
      return undefined;
    }
    const cancelToken = { cancelled: false };
    setIsLoadingDoctext(true);
    setDoctextError(null);
    setDoctext(null);
    const query = `?docType=${encodeURIComponent(
      docType
    )}&docVersion=${encodeURIComponent(currentVersion)}`;
    fetchOurApi({
      path: `/eula/doctext${query}`,
      method: "GET"
    })
      .then((res) => {
        if (cancelToken.cancelled) {
          return;
        }
        if (!res || res.error) {
          const code =
            (res && res.error && res.error.code) ||
            (res && res.error && res.error.name) ||
            "eula_doctext_missing";
          Sentry.captureException(
            (res && res.error) || new Error("eula doctext fetch failed"),
            {
              tags: { feature: `${analyticsEventPrefix}-eula-doctext-load` },
              extra: { accountId, tier, docType, docVersion: currentVersion }
            }
          );
          setDoctextError(code);
          setIsLoadingDoctext(false);
          return;
        }
        doctextCacheRef.current.set(cacheKey, res.doctext);
        setDoctext(res.doctext);
        setIsLoadingDoctext(false);
      })
      .catch((err) => {
        if (cancelToken.cancelled) {
          return;
        }
        Sentry.captureException(err, {
          tags: { feature: `${analyticsEventPrefix}-eula-doctext-load` },
          extra: { accountId, tier, docType, docVersion: currentVersion }
        });
        setDoctextError("eula_doctext_missing");
        setIsLoadingDoctext(false);
      });
    return () => {
      cancelToken.cancelled = true;
    };
  }, [
    open,
    docType,
    currentVersion,
    statusUnavailable,
    analyticsEventPrefix,
    accountId,
    tier
  ]);

  useEffect(() => {
    if (!open || hasFiredModalOpenedRef.current) {
      return;
    }
    hasFiredModalOpenedRef.current = true;
    const eventName = isReviewMode
      ? `${analyticsEventPrefix}_eula_review_opened`
      : `${analyticsEventPrefix}_eula_modal_opened`;
    analyticsTrack(eventName, { accountId, tier, docType, currentVersion });
  }, [
    open,
    isReviewMode,
    analyticsEventPrefix,
    accountId,
    tier,
    docType,
    currentVersion
  ]);

  useEffect(() => {
    if (!open || !isAcceptMode || hasDoctextError) {
      setShowSkipToEnd(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowSkipToEnd(true);
    }, SKIP_TO_END_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, isAcceptMode, hasDoctextError]);

  const scrollHelperCaption = useMemo(
    () => buildScrollHelperCaption({ scrolledToBottom, checkboxChecked }),
    [scrolledToBottom, checkboxChecked]
  );

  const onScrollHandler = (event) => {
    const target = event.target;
    const isNearBottom =
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - SCROLL_TO_BOTTOM_THRESHOLD_PX;
    if (!isNearBottom) {
      return;
    }
    setScrolledToBottom(true);
    if (hasFiredScrolledToBottomRef.current) {
      return;
    }
    hasFiredScrolledToBottomRef.current = true;
    analyticsTrack(`${analyticsEventPrefix}_eula_scrolled_to_bottom`, {
      accountId,
      tier,
      docType,
      currentVersion
    });
  };

  const onCheckboxToggle = () => {
    const nextValue = !checkboxChecked;
    setCheckboxChecked(nextValue);
    if (!nextValue) {
      return;
    }
    analyticsTrack(`${analyticsEventPrefix}_eula_checkbox_checked`, {
      accountId,
      tier,
      docType,
      currentVersion
    });
  };

  const onSkipToEnd = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    setScrolledToBottom(true);
    analyticsTrack(`${analyticsEventPrefix}_eula_skip_to_end_used`, {
      accountId,
      tier,
      docType,
      currentVersion
    });
  };

  const surfaceSnackbar = (severity, message) => {
    if (!activateSnackbar) {
      return;
    }
    activateSnackbar({ severity, message });
  };

  const handleAcceptError = (errObj) => {
    if (errObj.code === "version_stale") {
      surfaceSnackbar(
        "info",
        `The ${agreementNoun} was updated while you were reviewing it — please reopen to review the latest version.`
      );
      onAccepted();
      return;
    }
    Sentry.captureException(errObj, {
      tags: { feature: `${analyticsEventPrefix}-eula-accept` },
      extra: { accountId, tier, docType, docVersion: currentVersion }
    });
    surfaceSnackbar(
      "error",
      errObj.message ||
        `Couldn't record ${agreementNoun} acceptance. Please try again.`
    );
  };

  const toAcceptErrorEnvelope = (err) => {
    const networkError =
      err instanceof Error ? err : new Error("Network request failed");
    networkError.code = networkError.code || "network";
    return { error: networkError };
  };

  const onAcceptClick = async () => {
    setIsAccepting(true);
    const res = await fetchOurApi({
      path: "/eula/accept",
      method: "POST",
      data: { companyId: accountId, docType, docVersion: currentVersion }
    }).catch(toAcceptErrorEnvelope);
    setIsAccepting(false);
    const errObj = res && res.error;
    if (!res || errObj) {
      handleAcceptError(errObj || { code: "unknown" });
      return;
    }
    analyticsTrack(`${analyticsEventPrefix}_eula_accepted`, {
      accountId,
      tier,
      docType,
      currentVersion
    });
    onAccepted();
  };

  const onCloseClick = (_event, reason) => {
    if (reason !== "escapeKeyDown" && reason !== "backdropClick") {
      analyticsTrack(`${analyticsEventPrefix}_eula_modal_cancelled`, {
        accountId,
        tier,
        docType,
        currentVersion
      });
    }
    onClose();
  };

  const dialogTitle = buildTitle({
    mode,
    hasDoctextError,
    agreementNoun,
    currentVersion
  });

  const acceptDisabled =
    hasDoctextError ||
    isLoadingDoctext ||
    isAccepting ||
    !scrolledToBottom ||
    !checkboxChecked;

  return (
    <Dialog
      open={open}
      onClose={onCloseClick}
      fullWidth
      maxWidth="md"
      aria-labelledby="eula-modal-title"
    >
      <DialogTitle id="eula-modal-title">{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        {hasDoctextError && (
          <Box role="alert">
            <Typography variant="body2" color="textSecondary">
              {buildErrorCopy(agreementNoun)}
            </Typography>
          </Box>
        )}
        {!hasDoctextError && isLoadingDoctext && (
          <Stack alignItems="center" spacing={1} py={4}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="textSecondary">
              Loading the {agreementNoun}…
            </Typography>
          </Stack>
        )}
        {!hasDoctextError && !isLoadingDoctext && doctext && (
          <Stack spacing={2}>
            {isReviewMode && (
              <Typography variant="body2" color="textSecondary">
                {buildReviewCaption({ eulaStatus })}
              </Typography>
            )}
            <Box
              ref={scrollContainerRef}
              onScroll={onScrollHandler}
              sx={{
                overflow: "auto",
                maxHeight: "60vh",
                border: 1,
                borderColor: palette.divider,
                padding: 2,
                borderRadius: 1
              }}
              tabIndex={0}
            >
              <Markdown remarkPlugins={[remarkGfm]}>{doctext}</Markdown>
            </Box>
            {isAcceptMode && showSkipToEnd && !scrolledToBottom && (
              <Button
                size="small"
                variant="text"
                onClick={onSkipToEnd}
                aria-label="Skip to end of agreement"
              >
                Skip to end
              </Button>
            )}
            {isAcceptMode && (
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxChecked}
                      onChange={onCheckboxToggle}
                      color="primary"
                      disabled={isAccepting}
                    />
                  }
                  label="I have read and accept the terms"
                />
                {scrollHelperCaption && (
                  <Typography variant="caption" color="textSecondary">
                    {scrollHelperCaption}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCloseClick(null, "closeButton")} color="inherit">
          Close
        </Button>
        {isAcceptMode && !hasDoctextError && (
          <LoadingButton
            label="Accept"
            color="primary"
            onClick={onAcceptClick}
            isLoading={isAccepting}
            disabled={acceptDisabled}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

EulaAcceptanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["accept", "review"]).isRequired,
  tier: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  eulaStatus: PropTypes.shape({
    currentVersion: PropTypes.string,
    requiresAcceptance: PropTypes.bool,
    statusUnavailable: PropTypes.bool,
    acceptedAt: PropTypes.string,
    acceptedBy: PropTypes.string,
    acceptedByName: PropTypes.string
  }),
  analyticsEventPrefix: PropTypes.string,
  activateSnackbar: PropTypes.func,
  onAccepted: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default EulaAcceptanceModal;
